import {
  IdlingReportsApiFactory,
  ApiAppIdlingreportsGetidlingoffdutyaveragePostRequest,
  ApiAppIdlingreportsGetidlingdatafordriversPostRequest,
  DutyIdleSummaryDto,
  AverageIdleTimeDto,
  TotalFuelConsumptionDto,
  TotalFuelPercentageInIdleStateDto,
  OnDutyOffDutyIdleTimeSpentDto,
  IdleFuelConsumptionDto,
  WorstPerformersDto,
  AllDriversDtoPagedResultDto,
  PeriodType,
} from "../../../common/data/service-proxies";
import { useApiRequest } from "./useApiRequest";
import type { BaseHookReturn } from "./useApiRequest";

export interface IdleReportsHookParams {
  fromDate: Date;
  toDate: Date;
  periodType?: PeriodType;
}

export interface IdleReportsDriversHookParams extends IdleReportsHookParams {
  skipCount?: number;
  maxResultCount?: number;
}

function toCommonRequest(
  params: IdleReportsHookParams,
): ApiAppIdlingreportsGetidlingoffdutyaveragePostRequest {
  return {
    startTime: params.fromDate.toISOString(),
    endTime: params.toDate.toISOString(),
    periodType: params.periodType,
  };
}

/** GetIdlingOffDutyAverage */
export const useIdlingOffDutyAverage = (
  params: IdleReportsHookParams,
): BaseHookReturn<DutyIdleSummaryDto> => {
  return useApiRequest<DutyIdleSummaryDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetidlingoffdutyaveragePost(request);
      return response.data;
    },
    { logLabel: "useIdlingOffDutyAverage" },
  );
};

/** GetIdlingOnDutyAverage */
export const useIdlingOnDutyAverage = (
  params: IdleReportsHookParams,
): BaseHookReturn<DutyIdleSummaryDto> => {
  return useApiRequest<DutyIdleSummaryDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetidlingondutyaveragePost(request);
      return response.data;
    },
    { logLabel: "useIdlingOnDutyAverage" },
  );
};

/** GetTotalIdlingFuelConsumption */
export const useTotalIdlingFuelConsumption = (
  params: IdleReportsHookParams,
): BaseHookReturn<TotalFuelConsumptionDto> => {
  return useApiRequest<TotalFuelConsumptionDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGettotalidlingfuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useTotalIdlingFuelConsumption" },
  );
};

/** GetMaxIdlingTime */
export const useMaxIdlingTime = (
  params: IdleReportsHookParams,
): BaseHookReturn<DutyIdleSummaryDto> => {
  return useApiRequest<DutyIdleSummaryDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetmaxidlingtimePost(request);
      return response.data;
    },
    { logLabel: "useMaxIdlingTime" },
  );
};

/** GetMinIdlingTime */
export const useMinIdlingTime = (
  params: IdleReportsHookParams,
): BaseHookReturn<DutyIdleSummaryDto> => {
  return useApiRequest<DutyIdleSummaryDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetminidlingtimePost(request);
      return response.data;
    },
    { logLabel: "useMinIdlingTime" },
  );
};

/** GetAverageIdlingTime */
export const useAverageIdlingTime = (
  params: IdleReportsHookParams,
): BaseHookReturn<DutyIdleSummaryDto> => {
  return useApiRequest<DutyIdleSummaryDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetaverageidlingtimePost(request);
      return response.data;
    },
    { logLabel: "useAverageIdlingTime" },
  );
};

/** GetAverageIdlingTimeForTimeOfDay */
export const useAverageIdlingTimeForTimeOfDay = (
  params: IdleReportsHookParams,
): BaseHookReturn<AverageIdleTimeDto> => {
  return useApiRequest<AverageIdleTimeDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetaverageidlingtimefortimeofdayPost(request);
      return response.data;
    },
    { logLabel: "useAverageIdlingTimeForTimeOfDay" },
  );
};

/** GetFuelPercentageForIdlingTime */
export const useFuelPercentageForIdlingTime = (
  params: IdleReportsHookParams,
): BaseHookReturn<TotalFuelPercentageInIdleStateDto> => {
  return useApiRequest<TotalFuelPercentageInIdleStateDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetfuelpercentageforidlingtimePost(request);
      return response.data;
    },
    { logLabel: "useFuelPercentageForIdlingTime" },
  );
};

/** GetWorstPerformers (idling) */
export const useIdlingWorstPerformers = (
  params: IdleReportsHookParams,
): BaseHookReturn<WorstPerformersDto[]> => {
  return useApiRequest<WorstPerformersDto[]>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetworstperformersPost(request);
      return response.data;
    },
    { logLabel: "useIdlingWorstPerformers" },
  );
};

/** GetIdlingTimeByDuty */
export const useIdlingTimeByDuty = (
  params: IdleReportsHookParams,
): BaseHookReturn<OnDutyOffDutyIdleTimeSpentDto> => {
  return useApiRequest<OnDutyOffDutyIdleTimeSpentDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetidlingtimebydutyPost(request);
      return response.data;
    },
    { logLabel: "useIdlingTimeByDuty" },
  );
};

/** GetIdlingFuelConsumption */
export const useIdlingFuelConsumption = (
  params: IdleReportsHookParams,
): BaseHookReturn<IdleFuelConsumptionDto> => {
  return useApiRequest<IdleFuelConsumptionDto>(
    [params.fromDate, params.toDate, params.periodType],
    async (axios, baseURL) => {
      const request = toCommonRequest(params);
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetidlingfuelconsumptionPost(request);
      return response.data;
    },
    { logLabel: "useIdlingFuelConsumption" },
  );
};

/** GetIdlingDataForDrivers */
export const useIdlingDataForDrivers = (
  params: IdleReportsDriversHookParams,
): BaseHookReturn<AllDriversDtoPagedResultDto> => {
  return useApiRequest<AllDriversDtoPagedResultDto>(
    [
      params.fromDate,
      params.toDate,
      params.periodType,
      params.skipCount,
      params.maxResultCount,
    ],
    async (axios, baseURL) => {
      const request: ApiAppIdlingreportsGetidlingdatafordriversPostRequest = {
        startTime: params.fromDate.toISOString(),
        endTime: params.toDate.toISOString(),
        periodType: params.periodType,
        skipCount: params.skipCount,
        maxResultCount: params.maxResultCount,
      };
      const response = await IdlingReportsApiFactory(
        undefined,
        baseURL,
        axios,
      ).apiAppIdlingreportsGetidlingdatafordriversPost(request);
      return response.data;
    },
    { logLabel: "useIdlingDataForDrivers" },
  );
};
